import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import CryptoJS from "crypto-js";
import { API_BASE_URL, SESSION_URL } from "../config/api-config";
import { formatPersonName } from "../public/lib/formatName";

const UserContext = createContext();

const buildInitials = (...parts) => {
  const tokens = parts
    .flatMap((part) =>
      String(part || "")
        .trim()
        .split(/[\s._-]+/)
        .filter(Boolean)
    )
    .filter(Boolean);

  if (tokens.length >= 2) {
    return `${tokens[0][0]}${tokens[1][0]}`.toUpperCase();
  }

  if (tokens.length === 1) {
    return tokens[0].slice(0, 2).toUpperCase();
  }

  return "GU";
};

export const UserProvider = ({ children }) => {
  const [userName, setUserName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserInfo = useCallback(async () => {
    try {
      const encryptedToken = localStorage.getItem("authToken");
      if (!encryptedToken) {
        setUserName("");
        setEmailAddress("");
        setFirstName("");
        setLastName("");
        setUserId(null);
        setLoading(false);
        return;
      }

      const bytes = CryptoJS.AES.decrypt(encryptedToken, "klargo-secret-key");
      const token = bytes.toString(CryptoJS.enc.Utf8);

      const sessionResponse = await axios.get(
        SESSION_URL,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/plain",
            "Content-Type": "application/json",
          },
        }
      );

      const fetchedUserName = sessionResponse.data?.result?.user?.userName;
      const fetchedFirstName = sessionResponse.data?.result?.user?.name;
      const fetchedLastName = sessionResponse.data?.result?.user?.surname;
      const email = sessionResponse.data?.result?.user?.emailAddress;
      const id = sessionResponse.data?.result?.user?.id;

      setUserName(fetchedUserName || "");
      setEmailAddress(email || "");
      setUserId(id || null);

      let normalizedFirstName = fetchedFirstName || "";
      let normalizedLastName = fetchedLastName || "";

      if (id) {
        try {
          const profileResponse = await axios.get(
            `${API_BASE_URL}/User/GetUserProfileById`,
            {
              params: { Id: id },
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: "text/plain",
                "Content-Type": "application/json",
              },
            }
          );

          const profile = profileResponse.data?.result;
          const profileEmail = profile?.emailAddress || email || "";
          const profileFirstName = profile?.name || "";
          const profileLastName = profile?.surname || "";

          // Some email-signup flows seed name/surname with the email address
          // until the user completes the profile. Treat those as "missing".
          normalizedFirstName =
            profileFirstName && profileFirstName !== profileEmail ? profileFirstName : "";
          normalizedLastName =
            profileLastName && profileLastName !== profileEmail ? profileLastName : "";
        } catch (profileError) {
          console.error("❌ Error fetching user profile:", profileError);
        }
      }

      // BankID/typed names often arrive in CAPS - normalise for display.
      setFirstName(formatPersonName(normalizedFirstName));
      setLastName(formatPersonName(normalizedLastName));

    } catch (error) {
      console.error("❌ Error fetching user info:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  // Another tab of this browser logged in or out (e.g. the tab the BankID app returned to):
  // follow it, so this tab does not keep asking for a login that already happened.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === "authToken" || event.key === null) fetchUserInfo();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [fetchUserInfo]);

  const refreshUserInfo = async () => {
    await fetchUserInfo();
  };

  const displayName =
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    userName ||
    "";
  const displayInitials =
    [firstName, lastName].some(Boolean)
      ? buildInitials(firstName, lastName)
      : buildInitials(userName, emailAddress ? emailAddress.split("@")[0] : "");

  return (
    <UserContext.Provider
      value={{
        userName,
        emailAddress,
        firstName,
        lastName,
        displayName,
        displayInitials,
        userId,
        refreshUserInfo,
        loading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
